import { COMPANY_BULK_MAX_IDS, type CompanyListItem } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Building2 } from 'lucide-react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { fmtDate } from '../../lib/format.js';
import { statusLabel, statusTone } from './company-status.js';

interface Props {
  readonly items: readonly CompanyListItem[];
  readonly canManage?: boolean;
  readonly selectedIds?: ReadonlySet<string>;
  readonly onToggle?: (id: string) => void;
  readonly onToggleAll?: (checked: boolean) => void;
  readonly onRowClick: (id: string) => void;
  /** Há busca/filtro ativos — diferencia "sem resultados" de "sem itens" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
}

/* Tabela de empresas sobre o primitive `Table` de @planuze/ui (ADR-0700). O nome é um
   `<button>` (navegação acessível por teclado); coluna numérica com tabular-nums. */
export function CompaniesTable({
  items,
  canManage,
  selectedIds,
  onToggle,
  onToggleAll,
  onRowClick,
  hasFilters,
  onClearFilters,
}: Props) {
  const copy = useCompaniesContent();
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<Building2 className="size-6" />}
        title={copy.common.noResultsTitle}
        description={copy.table.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.common.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<Building2 className="size-6" />}
        title={copy.table.emptyTitle}
        description={copy.table.emptyDescription}
      />
    );
  }
  const selection = selectedIds ?? new Set<string>();
  const bulkEnabled = canManage === true && selectedIds !== undefined;
  const selectedVisibleCount = items.filter((company) => selection.has(company.id)).length;
  const selectionTarget = Math.min(items.length, COMPANY_BULK_MAX_IDS);
  const allSelected = selectionTarget > 0 && selectedVisibleCount >= selectionTarget;
  const selectionFull = selectedVisibleCount >= COMPANY_BULK_MAX_IDS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {bulkEnabled && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onToggleAll?.(checked === true)}
                aria-label={copy.table.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.table.columnCompany}</TableHead>
          <TableHead>{copy.table.columnStatus}</TableHead>
          <TableHead>{copy.table.columnMembers}</TableHead>
          <TableHead>{copy.table.columnCreatedAt}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((company) => (
          <TableRow key={company.id}>
            {bulkEnabled && (
              <TableCell>
                <Checkbox
                  checked={selection.has(company.id)}
                  disabled={selectionFull && !selection.has(company.id)}
                  onCheckedChange={() => onToggle?.(company.id)}
                  aria-label={copy.table.selectRowAriaLabel(company.name)}
                />
              </TableCell>
            )}
            <TableCell>
              <button
                type="button"
                className="font-medium text-foreground transition-colors hover:underline"
                onClick={() => onRowClick(company.id)}
              >
                {company.name}
              </button>
            </TableCell>
            <TableCell>
              <StatusBadge variant={statusTone(company.status)}>
                {statusLabel(company.status, copy.status)}
              </StatusBadge>
            </TableCell>
            <TableCell numeric className="text-muted-foreground">
              {company.memberCount}
            </TableCell>
            <TableCell className="text-muted-foreground">{fmtDate(company.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
